import { msg } from "@lingui/core/macro";
import { z } from "zod";
import * as Sentry from "@sentry/react";
import { i18n } from "@lingui/core";
import type { MessageDescriptor } from "@lingui/core";
import { useDialog } from "@medoma/components/Dialog/useDialog";

export const knownErrorCodeSchema = z.enum([
  // Common
  "InvalidJson",
  "InvalidLatvianPersonasKods",
  "InvalidPatientType",
  "InvalidSwedishPersonalNumber",
  "InvalidSwedishTemporaryNumber",
  "InvalidSwissEMedicusNumber",
  "InvalidSwissPatientNumber",
  "PreconditionFailed",
  "UnhandledException",
  // Logistics
  "ActivityClosed",
  "ActivityGroupNotFound",
  "ActivityNotFound",
  "AdditionalAddressInformationIsRequired",
  "KeyToPatientIsRequired",
  "AlreadyTravelledToVisit",
  "AlreadyTravellingToVisit",
  "AddressLine1MustBeSet",
  "AuthenticationFailed",
  "CityMustBeSet",
  "ConflictingOccurrence",
  "DiagnosisIsRequired",
  "AssetNotFound",
  "DuplicateCareEpisodeId",
  "EmptyMessage",
  "InvalidAssetId",
  "AlreadyAllocated",
  "AssetAllocated",
  "AssetInactive",
  "CategoryHasAssets",
  "CategoryNotFound",
  "DuplicateAssetId",
  "DuplicateCategoryId",
  "DuplicateName",
  "InvalidCareEpisodeId",
  "InvalidCategoryId",
  "InvalidComment",
  "InvalidDescription",
  "NotAllocated",
  "SiteNotFound",
  "CancelledVisit",
  "FinishedVisit",
  "TravellingToOrOngoingVisit",
  "InvalidActivityCategory",
  "InvalidActivityId",
  "InvalidArea",
  "InvalidCompetence",
  "InvalidDate",
  "InvalidDateTime",
  "InvalidEndDate",
  "InvalidEndDateTime",
  "InvalidInvoicingCodeType",
  "InvalidLatitude",
  "InvalidLongitude",
  "InvalidName",
  "InvalidPatientStatus",
  "InvalidPostalCode",
  "InvalidRelativeName",
  "InvalidRouteStatus",
  "InvalidStartDate",
  "InvalidStartTime",
  "InvalidTime",
  "InvalidVisitStatus",
  "InvalidWard",
  "InformationIsRequired",
  "LifeCareIsRequired",
  "HasActiveCareEpisode",
  "HealthcareJourneyNumberIsRequired",
  "HomeCareIsRequired",
  "MissingCompetence",
  "InvalidActivityType",
  "InvalidAggregateType",
  "InvalidEmployeeSourceIdType",
  "InvalidOccurrenceGroup",
  "MissingHeader",
  "MissingRoute",
  "NoOccurrencesInSchedule",
  "OccurrenceFinished",
  "OccurrenceGroupCancelled",
  "OccurrenceGroupNotFound",
  "OccurrenceGroupNotAllocated",
  "OccurrenceGroupNotStarted",
  "OccurrenceNotAllocated",
  "OccurrenceNotAssigned",
  "UnassignedOccurrence",
  "OccurrenceNotFinished",
  "OccurrenceNotFound",
  "OccurrenceNotInGroup",
  "OccurrenceNotStarted",
  "OccurrenceMissingShift",
  "OccurrenceOutsideOfSchedule",
  "OccurrencePassed",
  "OccurrenceRemoved",
  "OngoingVisit",
  "OverlappingExecutionTimes",
  "RetroactiveActivityMustBeInPast",
  "GvrCareEpisodeMissing",
  "PatientMissingDiagnosis",
  "PatientNotFound",
  "PatientTypeInvoicingCodeMismatch",
  "PetIsRequired",
  "RelationNotSet",
  "RelativeIsRequired",
  "AtLeastOneRelativeIsRequired",
  "SafetyAlarmTextIsRequired",
  "SameTimeSpan",
  "ShiftNotFound",
  "TeamNotFound",
  "EmployeeNotFound",
  "ShiftEndsBeforeStart",
  "ProviderNotMedoma",
  "InvalidShiftType",
  "UnassignedShift",
  "ShiftOverlaps",
  "SwedishTemporaryIdentityNumberFeatureDisabled",
  "SupportAgentError",
  "TemplateNotFound",
  "TimeSpanNotPositive",
  "TooFarFromCareHub",
  "UnknownInvoicingCode",
  "UnstartedTripToVisit",
  "UnstartedVisit",
  "VisitNotFound",
  "NotSameDay",
  "HandledByInvoicing",
  "AlreadyPlanned",
  // Patient
  "DuplicateHealthcareJourneyNumber",
  "ExternalCareEpisodeIdIsRequired",
  "FunctionIsNotActive",
  "InvalidCoordinates",
  "InvalidDiagnosisCode",
  "InvalidEconomicUnit",
  "InvalidEdit",
  "InvalidField",
  "InvalidHealthcareJourneyNumber",
  "InvalidPhoneNumber",
  "InvalidSafetyAlarm",
  "InvalidTeam",
  "PatientNotFound",
  "RelativeIdConflict",
  "RelationMissing",
  "RelativeMissing",
  "RelativeNameMissing",
  "RequiredField",
  "TooFarAwayFromCareProvider",
  "NoActivitiesProvided",
  "DischargeDayBeforeAdmitted",
  "CannotDischargeInFuture",
  "CannotPauseInFuture",
  "PauseDayBeforeAdmitted",
  "ActivityNotPaused",
  "ScheduleExpired",
  "ActivityNotClosedDueToPause",
  "InvalidCloseReason",
  "ActivityToResumeNotFound",
  "AlreadyAdmitted",
  "AlreadyRegistered",
  "AdmissionDateBeforeRegistration",
  "AdmissionDateInFuture",
  "CloseContactIsRequired",
  "FlagsIsRequired",
  "IsReturningPatient",
]);
type KnownErrorCode = z.infer<typeof knownErrorCodeSchema>;
const knownErrorCodeDictionary: Record<
  KnownErrorCode,
  {
    issue: MessageDescriptor | "DEFAULT";
    resolution: MessageDescriptor | "NONE" | "DEFAULT";
  }
> = {
  // Common
  InvalidJson: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidLatvianPersonasKods: {
    issue: msg`Ogiltig personas kods.`,
    resolution: msg`Ange en giltig personas kods på formen XXXXXX-XXXXX.`,
  },
  InvalidSwedishPersonalNumber: {
    issue: msg`Ogiltigt personnummer.`,
    resolution: msg`Ange ett giltigt personnummer på formen ÅÅÅÅMMDDXXXX.`,
  },
  InvalidSwedishTemporaryNumber: {
    issue: msg`Ogiltigt reservnummer.`,
    resolution: msg`Ange ett giltigt reservnummer bestående av siffror och/eller bokstäver.`,
  },
  InvalidSwissEMedicusNumber: {
    issue: msg`Ogiltigt eMedicus-nummer.`,
    resolution: msg`Ange ett giltigt 6-siffrigt eMedicus-nummer.`,
  },
  InvalidSwissPatientNumber: {
    issue: msg`Ogiltigt patientnummer.`,
    resolution: msg`Ange ett giltigt patientnummer med 7-10 siffror.`,
  },
  PreconditionFailed: {
    issue: msg`Detta har ändrats sedan sidan laddades in.`,
    resolution: msg`Ladda om sidan och försök igen.`,
  },
  UnhandledException: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },

  // Logistics
  ActivityClosed: {
    issue: msg`Åtgärden kan inte utföras eftersom aktiviteten har avslutats.`,
    resolution: "NONE",
  },
  ActivityGroupNotFound: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  ActivityNotFound: {
    issue: msg`Aktiviteten kan inte hittas.`,
    resolution: msg`Ladda om sidan och prova igen.`,
  },
  AdditionalAddressInformationIsRequired: {
    issue: msg`Övrig adressinformation krävs.`,
    resolution: msg`Ange övrig adressinformation och försök igen.`,
  },
  KeyToPatientIsRequired: {
    issue: msg`Information av nyckel krävs.`,
    resolution: msg`Ange information om nyckel och försök igen.`,
  },
  AlreadyTravelledToVisit: {
    issue: msg`Resan till besöket är redan genomförd.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  AlreadyTravellingToVisit: {
    issue: msg`Resan till besöket är redan påbörjad.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  AddressLine1MustBeSet: {
    issue: msg`Adress krävs.`,
    resolution: msg`Ange en adress och försök igen.`,
  },
  AuthenticationFailed: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  CityMustBeSet: {
    issue: msg`Postort krävs.`,
    resolution: msg`Ange en postort och försök igen.`,
  },
  ConflictingOccurrence: {
    issue: msg`Det går inte att flytta aktiviteten till en tid som överlappar med ett annat tillfälle i serien.`,
    resolution: msg`Välj en tid som inte krockar med andra tillfällen.`,
  },
  DiagnosisIsRequired: {
    issue: msg`Diagnoskod krävs.`,
    resolution: msg`Ange en diagnoskod och försök igen.`,
  },
  AssetNotFound: {
    issue: msg`Utrustningen kan inte hittas.`,
    resolution: msg`Ladda om sidan och prova igen.`,
  },
  InvalidAssetId: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  AlreadyAllocated: {
    issue: msg`Utrustningen är redan tilldelad.`,
    resolution: "NONE",
  },
  AssetAllocated: {
    issue: msg`Utrustningen är tilldelad.`,
    resolution: "NONE",
  },
  AssetInactive: {
    issue: msg`Utrustningen är avaktiverad.`,
    resolution: "NONE",
  },
  CategoryHasAssets: {
    issue: msg`Utrustningskategorin har utrustning.`,
    resolution: msg`Töm kategorin på utrustning och försök sen igen.`,
  },
  CategoryNotFound: {
    issue: msg`Utrustningskategorin hittades inte.`,
    resolution: "DEFAULT",
  },
  DuplicateAssetId: {
    issue: msg`Utrustningen finns redan.`,
    resolution: "DEFAULT",
  },
  DuplicateCategoryId: {
    issue: msg`Utrustningskategorin finns redan.`,
    resolution: "DEFAULT",
  },
  DuplicateName: {
    issue: msg`Namnet används redan.`,
    resolution: msg`Välj ett annat namn och försök igen.`,
  },
  InvalidCareEpisodeId: {
    issue: "DEFAULT",
    resolution: "NONE",
  },
  InvalidCategoryId: {
    issue: "DEFAULT",
    resolution: "NONE",
  },
  InvalidComment: {
    issue: "DEFAULT",
    resolution: "NONE",
  },
  InvalidDescription: {
    issue: "DEFAULT",
    resolution: "NONE",
  },
  NotAllocated: {
    issue: msg`Utrustningen är inte tilldelad.`,
    resolution: "DEFAULT",
  },
  SiteNotFound: {
    issue: "DEFAULT",
    resolution: "NONE",
  },
  DuplicateCareEpisodeId: {
    issue: msg`Det finns redan en vårdperiod med samma id.`,
    resolution: msg`Avsluta den aktiva vårdperioden innan du försöker skapa en ny.`,
  },
  EmptyMessage: {
    issue: msg`Meddelandet som skickades var tomt.`,
    resolution: msg`Prova igen. Om det fortfarande inte fungerar, hör av dig till teknisk support.`,
  },
  CancelledVisit: {
    issue: msg`Besöket är redan avbokat.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  FinishedVisit: {
    issue: msg`Besöket är slutfört.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  TravellingToOrOngoingVisit: {
    issue: msg`Resan till besöket är påbörjad eller besöket är pågående.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  HomeCareIsRequired: {
    issue: msg`Information om sociala insatser krävs.`,
    resolution: msg`Lägg till information om sociala insatser och försök igen.`,
  },
  InformationIsRequired: {
    issue: msg`Allmän information krävs.`,
    resolution: msg`Lägg till allmän information och försök igen.`,
  },
  InvalidActivityCategory: {
    // Won't happen unless developer error.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidActivityId: {
    // Won't happen unless developer error.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidActivityType: {
    // Won't happen unless developer error.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidAggregateType: {
    // Won't happen unless developer error.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidArea: {
    issue: msg`Ogiltigt område.`,
    resolution: "DEFAULT",
  },
  InvalidCompetence: {
    // Won't happen since selectable competences are given by BE
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidDate: {
    issue: msg`Ogiltigt datum.`,
    resolution: "NONE",
  },
  InvalidDateTime: {
    // Behind the scenes.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidEmployeeSourceIdType: {
    issue: msg`Funktionen stöds inte för den konfigurerade schemaläggningstjänsten.`,
    resolution: msg`Kontakta teknisk support.`,
  },
  InvalidEndDate: {
    issue: msg`Ogiltigt slutdatum.`,
    resolution: "NONE",
  },
  InvalidEndDateTime: {
    issue: msg`Ogiltigt slutdatum.`,
    resolution: msg`Sluttiden måste vara senare än starttiden.`,
  },
  InvalidInvoicingCodeType: {
    issue: msg`Ogiltiga faktureringskoder. En KVÅ-kod har angivits som en produktkod, eller tvärtom.`,
    resolution: "DEFAULT",
  },
  InvalidLatitude: {
    issue: msg`Adressen är ogiltig.`,
    resolution: msg`Kontrollera att adressen är korrekt och att nålen är placerad på rätt plats på kartan.`,
  },
  InvalidLongitude: {
    issue: msg`Adressen är ogiltig.`,
    resolution: msg`Kontrollera att adressen är korrekt och att nålen är placerad på rätt plats på kartan.`,
  },
  InvalidName: {
    // Is used for changing route namn at time of commit.
    // Too generic name.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidOccurrenceGroup: {
    issue: msg`Besöket har antingen passerat, är utfört eller överlappar inte med aktivitetstillfällets tid.`,
    resolution: msg`Välj ett annat besök att flytta aktivitetstillfället till.`,
  },
  InvalidPatientStatus: {
    // Behind the scenes.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidPatientType: {
    issue: msg`Ogiltig patienttyp.`,
    resolution: "DEFAULT",
  },
  InvalidPostalCode: {
    issue: msg`Ogiltigt postnummer.`,
    resolution: msg`Ange ett giltigt postnummer.`,
  },
  InvalidRelativeName: {
    issue: msg`Ogiltigt namn på anhörig.`,
    resolution: msg`Ange ett giltigt namn för den anhöriga.`,
  },
  InvalidRouteStatus: {
    // Behind the scenes.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidStartDate: {
    issue: msg`Angivet datum har redan passerat.`,
    resolution: msg`Ange ett datum i framtiden.`,
  },
  InvalidStartTime: {
    issue: msg`Angiven tidpunkt har redan passerat.`,
    resolution: msg`Ange en tid i framtiden och försök igen.`,
  },
  InvalidTime: {
    issue: msg`Ogiltigt tidsformat.`,
    resolution: "NONE",
  },
  InvalidWard: {
    issue: msg`Ogiltig avdelning.`,
    resolution: "DEFAULT",
  },
  LifeCareIsRequired: {
    issue: msg`LifeCare krävs.`,
    resolution: msg`Lägg till LifeCare och försök igen.`,
  },
  HasActiveCareEpisode: {
    issue: msg`Patienten har en pågående vårdperiod.`,
    resolution: msg`Avsluta den pågående vårdperioden innan du försöker starta en ny.`,
  },
  HealthcareJourneyNumberIsRequired: {
    issue: msg`Sjukresenummer krävs.`,
    resolution: msg`Ange ett giltigt sjukresenummer och försök igen.`,
  },
  TimeSpanNotPositive: {
    issue: msg`Tidsspannet måste vara större än noll.`,
    resolution: msg`Ändra till ett större tidsspann.`,
  },
  InvalidVisitStatus: {
    // Behind the scenes
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  MissingCompetence: {
    issue: msg`Aktiviteten saknar kompetensbehov.`,
    resolution: msg`Ange minst en kompetens.`,
  },
  MissingHeader: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  MissingRoute: {
    issue: msg`Rutten finns inte.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  NoActivitiesProvided: {
    issue: msg`Inga aktiviteter är valda.`,
    resolution: msg`Lägg till minst en aktivitet och försök igen.`,
  },
  NoOccurrencesInSchedule: {
    issue: msg`Schemat resulterar inte i några aktivitetstillfällen.`,
    resolution: msg`Se över start- och slutdatum och/eller frekvens så att minst ett tillfälle ryms i schemat.`,
  },
  OccurrenceFinished: {
    issue: msg`Aktiviteten har avslutats.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  OccurrenceGroupCancelled: {
    issue: msg`Besöket är avbokat.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  OccurrenceGroupNotFound: {
    issue: msg`Hittade inte besöket.`,
    resolution: msg`Ladda om sidan och försök igen.`,
  },
  OccurrenceGroupNotAllocated: {
    issue: msg`Besöket har inte tilldelats någon.`,
    resolution: msg`Lägg till den som utförde besöket och försök igen.`,
  },
  OccurrenceGroupNotStarted: {
    issue: msg`Det går inte att avsluta besöket innan dess starttid.`,
    resolution: msg`Vänta tills besökets starttid har passerat och försök igen.`,
  },
  OccurrenceNotAssigned: {
    issue: msg`Aktiviteten har inte tilldelats någon.`,
    resolution: "NONE",
  },
  UnassignedOccurrence: {
    issue: msg`Aktiviteten har inte tilldelats någon.`,
    resolution: msg`Tilldela aktiviteten till den som utförde den och försök igen.`,
  },
  OccurrenceNotAllocated: {
    issue: msg`Aktiviteten har inte tilldelats någon.`,
    resolution: msg`Lägg till den som utförde aktiviteten och försök igen.`,
  },
  OccurrenceNotFinished: {
    issue: msg`Aktivitetstillfället är inte avslutat.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  OccurrenceOutsideOfSchedule: {
    issue: msg`Det går inte att flytta aktiviteten till den valda tiden eftersom den ligger utanför seriens start- och slutdatum.`,
    resolution: "NONE",
  },
  OccurrenceNotFound: {
    issue: msg`Hittade inte aktivitetstillfället.`,
    resolution: "DEFAULT",
  },
  OccurrenceNotInGroup: {
    issue: msg`Ett av aktivitetstillfällena tillhör inte besöket.`,
    resolution: msg`Ladda om sidan och försök igen. Kontakta support om felet kvarstår.`,
  },
  OccurrenceNotStarted: {
    issue: msg`Det går inte att avsluta besöket innan dess starttid.`,
    resolution: msg`Vänta tills besökets starttid har passerat och försök igen.`,
  },
  OccurrenceMissingShift: {
    issue: msg`Ett av aktivitetstillfällena har inte tilldelats någon.`,
    resolution: msg`Ladda om sidan och försök igen. Kontakta support om felet kvarstår.`,
  },
  OccurrencePassed: {
    issue: msg`Tiden för aktivitetstillfället har redan passerat.`,
    resolution: "NONE",
  },
  OccurrenceRemoved: {
    issue: msg`Aktivitetstillfället är borttaget.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  OngoingVisit: {
    issue: msg`Besök är pågående.`,
    resolution: msg`Prata med berörd kollega om ändringar ändå krävs.`,
  },
  OverlappingExecutionTimes: {
    issue: msg`Tidsflexibiliteten sträcker sig över flera tidstillfällen.`,
    resolution: msg`Justera tiderna eller minska tidsflexibiliteten och försök igen.`,
  },
  PatientNotFound: {
    issue: msg`Hittade inte patienten.`,
    resolution: "DEFAULT",
  },
  PatientTypeInvoicingCodeMismatch: {
    issue: msg`Faktureringskoderna är inte giltiga för denna patienttyp.`,
    resolution: "DEFAULT",
  },
  PetIsRequired: {
    issue: msg`Information om husdjur krävs.`,
    resolution: msg`Lägg till information om husdjur och försök igen.`,
  },
  RelationNotSet: {
    issue: msg`Anhörigs relation till patienten saknas.`,
    resolution: msg`Ange den anhörigas relation till patienten.`,
  },
  RelativeIsRequired: {
    issue: msg`Anhörig krävs.`,
    resolution: msg`Lägg till en anhörig och försök igen.`,
  },
  AtLeastOneRelativeIsRequired: {
    issue: msg`Patienten måste ha minst en anhörig.`,
    resolution: msg`Lägg till en annan anhörig innan du tar bort den här.`,
  },
  SameTimeSpan: {
    issue: msg`Tidsspannet är oförändrat.`,
    resolution: msg`Ändra till ett annat tidsspann, eller avbryt ändringen om tidsspannet redan är som önskat.`,
  },
  SafetyAlarmTextIsRequired: {
    issue: msg`Säkerhetslarm krävs.`,
    resolution: msg`Lägg till säkerhetslarm och försök igen.`,
  },
  SwedishTemporaryIdentityNumberFeatureDisabled: {
    issue: msg`Funktionen för reservnummer är inaktiverad.`,
    resolution: "DEFAULT",
  },
  ShiftNotFound: {
    issue: msg`Arbetspass kunde ej hittas.`,
    resolution: "DEFAULT",
  },
  TeamNotFound: {
    issue: msg`Teamet kunde ej hittas.`,
    resolution: msg`Ladda om sidan och försök igen. Kontakta support om felet kvarstår.`,
  },
  EmployeeNotFound: {
    issue: msg`Medarbetaren kunde ej hittas.`,
    resolution: msg`Ladda om sidan och försök igen. Kontakta support om felet kvarstår.`,
  },
  ShiftEndsBeforeStart: {
    issue: msg`Arbetspassets sluttid är innan dess starttid.`,
    resolution: msg`Ändra tiderna så att starttiden är före sluttiden och försök igen.`,
  },
  ProviderNotMedoma: {
    issue: msg`Arbetspasset kommer från ett externt schemasystem.`,
    resolution: "NONE",
  },
  InvalidShiftType: {
    issue: msg`Ogiltig typ av arbetspass.`,
    resolution: msg`Ladda om sidan och försök igen. Kontakta support om felet kvarstår.`,
  },
  UnassignedShift: {
    issue: msg`Arbetspasset har inte tilldelats någon.`,
    resolution: msg`Tilldela arbetspasset till någon och försök igen.`,
  },
  ShiftOverlaps: {
    issue: msg`Överlappande arbetspass.`,
    resolution: msg`Den valda medarbetaren är redan tilldelad ett annat pass som överlappar med detta.`,
  },
  SupportAgentError: {
    issue: msg`Ett fel uppstod när Handboken skulle svara.`,
    resolution: "DEFAULT",
  },
  TemplateNotFound: {
    issue: msg`Mallen hittades inte.`,
    resolution: msg`Ladda om sidan och försök igen. Kontakta support om felet kvarstår.`,
  },
  UnknownInvoicingCode: {
    issue: msg`Okänd faktureringskod.`,
    resolution: "DEFAULT",
  },
  UnstartedTripToVisit: {
    issue: msg`Resan till besöket har inte påbörjats.`,
    resolution: msg`Säkerställ att resan har påbörjats innan du fortsätter.`,
  },
  UnstartedVisit: {
    issue: msg`Besöket är inte påbörjat.`,
    resolution: msg`Säkerställ att besöket har påbörjats innan du fortsätter.`,
  },
  VisitNotFound: {
    issue: msg`Besöket hittades inte.`,
    resolution: msg`Stäm av med Center vid behov.`,
  },
  NotSameDay: {
    issue: msg`Besöket kan bara ångras samma dag som det slutfördes.`,
    resolution: "NONE",
  },
  HandledByInvoicing: {
    issue: msg`Besöket har redan hanterats av faktureringen och kan inte ångras.`,
    resolution: "NONE",
  },
  AlreadyPlanned: {
    issue: msg`Besöket är redan planerat.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },

  // Patient
  DuplicateHealthcareJourneyNumber: {
    issue: msg`Sjukresenumret används av en annan patient.`,
    resolution: msg`Ta ett nytt sjukresekort och försök igen.`,
  },
  ExternalCareEpisodeIdIsRequired: {
    issue: msg`Externt vårdperiodnummer krävs.`,
    resolution: msg`Ange ett externt vårdperiodnummer och försök igen.`,
  },
  FunctionIsNotActive: {
    issue: msg`Funktionen är inte aktiv.`,
    resolution: "DEFAULT",
  },
  InvalidCoordinates: {
    issue: msg`Ogiltiga koordinater.`,
    resolution: msg`Latitud bör vara mellan -90 och 90, longitud mellan -180 och 180.`,
  },
  InvalidDiagnosisCode: {
    issue: msg`Ogiltig diagnoskod.`,
    resolution: msg`Dubbelkolla diagnoskoden och hör av dig till teknisk support om det borde fungera.`,
  },
  InvalidEconomicUnit: {
    issue: msg`Ogiltig ekonomisk enhet.`,
    resolution: msg`Dubbelkolla den ekonomiska enheten och hör av dig till teknisk support om det borde fungera.`,
  },
  InvalidEdit: {
    issue: msg`Ogiltig ändring.`,
    resolution: msg`Om du försökte ändra flera saker på samma gång, försök att ändra en sak i taget istället.`,
  },
  InvalidField: {
    issue: msg`Ogiltigt fält.`,
    resolution: "DEFAULT",
  },
  InvalidHealthcareJourneyNumber: {
    issue: msg`Ogiltigt sjukresenummer.`,
    resolution: msg`Kontrollera att sjukresenumret är korrekt och försök igen.`,
  },
  InvalidPhoneNumber: {
    issue: msg`Ogiltigt telefonnummer.`,
    resolution: msg`Kontrollera att telefonnumret är korrekt och försök igen.`,
  },
  InvalidSafetyAlarm: {
    // Empty string submitted. Doesn't happen and if it does it is an implementation error.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidTeam: {
    issue: msg`Ogiltigt team.`,
    resolution: msg`Kontrollera att teamet är ifyllt korrekt och försök igen.`,
  },
  RelationMissing: {
    issue: msg`Anhörigs relation saknas.`,
    resolution: msg`Specificera den anhörigas relation till patienten.`,
  },
  RelativeIdConflict: {
    issue: msg`Anhörig med samma id existerar redan.`,
    resolution: msg`Ladda om sidan och försök igen.`,
  },
  RelativeMissing: {
    issue: msg`Anhörig hittades inte.`,
    resolution: "DEFAULT",
  },
  RelativeNameMissing: {
    issue: msg`Anhörig saknar namn.`,
    resolution: msg`Ange namn för den anhöriga.`,
  },
  RetroactiveActivityMustBeInPast: {
    issue: msg`Aktiviteten kan inte registreras i framtiden.`,
    resolution: msg`Ange ett datum i det förflutna för aktiviteten.`,
  },
  RequiredField: {
    issue: msg`Får inte vara tomt.`,
    resolution: msg`Kontrollera att allt som inte är valfritt har fyllts i.`,
  },
  TooFarFromCareHub: {
    issue: msg`Angiven adress ligger för långt bort.`,
    resolution: msg`Kontrollera att adressen är inom rimligt avstånd från den klinik eller enhet som är angiven som utgångspunkt.`,
  },
  TooFarAwayFromCareProvider: {
    issue: msg`Angiven adress ligger utanför vårdgivarens definierade upptagningsområde.`,
    resolution: msg`Säkerställ att adressen ligger inom området som vårdgivaren täcker.`,
  },
  DischargeDayBeforeAdmitted: {
    issue: msg`Datumet är tidigare än inskrivningsdatum.`,
    resolution: msg`Ange ett datum som är senare än inskrivningsdatum.`,
  },
  PauseDayBeforeAdmitted: {
    issue: msg`Datumet är tidigare än inskrivningsdatum.`,
    resolution: msg`Ange ett datum som är senare än inskrivningsdatum.`,
  },
  CannotDischargeInFuture: {
    issue: msg`Datumet är i framtiden.`,
    resolution: msg`Ange ett datum som är idag eller tidigare.`,
  },
  CannotPauseInFuture: {
    issue: msg`Datumet är i framtiden.`,
    resolution: msg`Ange ett datum som är idag eller tidigare.`,
  },
  ActivityNotPaused: {
    issue: msg`En eller flera aktiviteter är inte pausade.`,
    // TODO: med-6840-fully-fledged-error-handling: point to which activity is
    // the problem, and allow recovery
    resolution: "NONE",
  },
  ScheduleExpired: {
    issue: msg`En eller flera aktiviteter har passerat sin sluttid.`,
    // TODO: med-6840-fully-fledged-error-handling: point to which activity is
    // the problem, and allow recovery
    resolution: "NONE",
  },
  ActivityNotClosedDueToPause: {
    issue: msg`En eller flera aktiviteter stängdes inte på grund av paus.`,
    // TODO: med-6840-fully-fledged-error-handling: point to which activity is
    // the problem, and allow recovery
    resolution: "NONE",
  },
  // Happens when API receives a closeReason that is not `pause` or `close`
  // at the time of writing. Not a user error.
  InvalidCloseReason: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  // Activity retrieval to populate the checklist that the user chooses from
  // has presented an option which is no longer in the database
  // Not a user error.
  // TODO: med-6840-fully-fledged-error-handling: point to which activity is
  // the issue, and allow recovery
  ActivityToResumeNotFound: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  AlreadyAdmitted: {
    issue: msg`Patienten är redan inskriven.`,
    resolution: "NONE",
  },
  AlreadyRegistered: {
    issue: msg`Patienten är redan registrerad.`,
    resolution: "NONE",
  },
  AdmissionDateBeforeRegistration: {
    issue: msg`Inskrivningsdatumet är tidigare än när patienten registrerades.`,
    resolution: msg`Välj registreringsdagen eller en senare dag.`,
  },
  AdmissionDateInFuture: {
    issue: msg`Inskrivningsdatumet ligger i framtiden.`,
    resolution: msg`Välj dagens datum eller en tidigare dag.`,
  },
  CloseContactIsRequired: {
    issue: msg`Anhörig krävs.`,
    resolution: msg`Lägg till en anhörig och försök igen.`,
  },
  FlagsIsRequired: {
    issue: msg`Flaggningar krävs.`,
    resolution: msg`Lägg till flaggningar och försök igen.`,
  },
  IsReturningPatient: {
    issue: msg`Patienten har redan en vårdperiod hos er.`,
    resolution: msg`Återregistrera patienten i stället för att registrera en ny.`,
  },
  // INVOICING
  GvrCareEpisodeMissing: {
    issue: msg`Patienten saknar GVR-vårdperiod.`,
    resolution: msg`Hör av dig till teknisk support för att hantera detta.`,
  },
  PatientMissingDiagnosis: {
    issue: msg`Patienten saknar diagnoskod.`,
    resolution: msg`Åtgärda detta och försök igen.`,
  },
};

const responseWithKnownErrorCodeSchema = z.object({
  data: z.object({
    code: knownErrorCodeSchema,
  }),
});

const responseWithStatusSchema = z.object({
  response: z.object({
    status: z.number(),
  }),
});

export const isKnownErrorCode = (
  errorCode: unknown,
): errorCode is keyof typeof knownErrorCodeDictionary => {
  return knownErrorCodeSchema.safeParse(errorCode).success;
};

export const isErrorWithKnownErrorCode = (
  error: unknown,
): error is { data: { code: KnownErrorCode } } => {
  return responseWithKnownErrorCodeSchema.safeParse(error).success;
};

const isErrorReponseWithStatus = (
  error: unknown,
): error is { response: { status: number } } => {
  return responseWithStatusSchema.safeParse(error).success;
};

export const isClientSideError = (error: unknown) => {
  return (
    isErrorReponseWithStatus(error) &&
    error.response.status >= 400 &&
    error.response.status < 500
  );
};

const formatKnownError = ({
  issue,
  resolution,
  defaults,
}: (typeof knownErrorCodeDictionary)[keyof typeof knownErrorCodeDictionary] & {
  defaults: {
    issue: MessageDescriptor;
    resolution: MessageDescriptor;
  };
}) => {
  const userFacingErrorExplanation = i18n._(
    issue === "DEFAULT" ? defaults.issue : issue,
  );
  const userFacingErrorResolution =
    resolution === "NONE"
      ? ""
      : resolution === "DEFAULT"
        ? i18n._(defaults.resolution)
        : i18n._(resolution);
  return `${userFacingErrorExplanation}${userFacingErrorResolution ? ` ${userFacingErrorResolution}` : ""}`;
};

export const deducedError = (
  error: unknown,
  defaults?: {
    issue: MessageDescriptor | undefined;
    resolution: MessageDescriptor | undefined;
  },
) => {
  const defaultUserFacingErrorExplanation =
    defaults?.issue ?? msg`Något gick fel.`;
  const defaultUserFacingErrorResolution =
    defaults?.resolution ??
    msg`Försök igen om en liten stund och hör av dig till teknisk support om det ändå inte fungerar.`;

  if (isKnownErrorCode(error)) {
    return formatKnownError({
      ...knownErrorCodeDictionary[error],
      defaults: {
        issue: defaultUserFacingErrorExplanation,
        resolution: defaultUserFacingErrorResolution,
      },
    });
  }

  if (isErrorWithKnownErrorCode(error)) {
    return formatKnownError({
      ...knownErrorCodeDictionary[error.data.code],
      defaults: {
        issue: defaultUserFacingErrorExplanation,
        resolution: defaultUserFacingErrorResolution,
      },
    });
  }

  return `${i18n._(defaultUserFacingErrorExplanation)} ${i18n._(defaultUserFacingErrorResolution)}`;
};

export const useErrorMessageAlert = () => {
  const { alert } = useDialog();

  const displayErrorMessageAlert = (message: string) => {
    Sentry.captureException(new Error(`An error alert was shown: ${message}`));
    alert({
      title: i18n._(msg`Något gick fel`),
      message,
    });
  };

  return { displayErrorMessageAlert };
};
